import incrementAchievement from '../services/achievementService';
import addNotification from '../services/notificationService';
import { Achievement, Notification, NotificationType, User } from '../types';

/**
 * Interface for the AchievementTracking hook.
 */
interface AchievementTracking {
  achievementType: string;
}

/**
 * Custom hook that handles the achievement tracking and incrementing.
 * @param achievementType The type of achievement to track.
 * @returns an object containing the function to add an achievement if earned.
 */
const useAchievementTracking = ({ achievementType }: AchievementTracking) => {
  const addAchievementIfEarned = async (
    sender: User,
    receiver: User,
    additionalFilter: (val: Achievement) => boolean = (val: Achievement) => true,
    incrementFilter: (val: Achievement) => boolean = achievement =>
      achievement.goal - achievement.progress > 0,
  ) => {
    await Promise.all(
      receiver.achievements
        .filter(achievement => achievement.awarded === false)
        .filter(additionalFilter)
        .filter(achievement => achievement.icon.includes(achievementType))
        .map(async achievement => {
          const notif: Notification = {
            message: `You have earned the achievement: ${achievement.name}`,
            sender,
            receiver,
            notificationType: NotificationType.Achievement,
            notificationDateTime: new Date(),
            path: '/profile/{receiver}',
          };
          return addNotification(notif);
        }),
    );

    // increment all relevant achievements
    await Promise.all(
      receiver.achievements
        .filter(achievement => achievement.awarded === false)
        .filter(incrementFilter)
        .filter(achievement => achievement.icon.includes(achievementType))
        .map(async achievement => {
          if (achievement._id) {
            incrementAchievement(achievement._id);
          }
        }),
    );
  };

  return {
    addAchievementIfEarned,
  };
};

export default useAchievementTracking;
